import SearchIcon from "@Icons/SearchIcon";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { MAP_ID, PAGE } from "constants/configConstants";
import useClickOutside from "hooks/useOutsideClick";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { getRegions } from "services";
import getRegion from "services/getRegions";
import SectionContainer from "../SectionContainer";
import Map from "./components/Map";
import MapInfoTabs from "./components/MapInfoTabs";
import MapModal from "./components/MapModal";
import mapLoaded from "./components/RoadViewMap/util";
import ToggleTab from "./components/ToggleTab";
import { useLocationContext } from "./locationContext";
import styles from "./mapSection.module.scss";
import { getFilteredUniversityFromLocal, getMapMarkerIconUrl } from "./util";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import calculateDistance from "../MapSection/util";
import { isEmptyObject } from "helpers/validations";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import useCookies from "hooks/useCookies";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE, CLICKED_ITEM } from "@Utils/webApputils/analyticEvents";
//

declare let google: any;

const INITIAL_TABS = [
  {
    name: "University",
    items: [],
  },
];

export const MapLocationV2 = ({ isDesktop = false }) => {
  // const [mapType, setMapType] = useState(MAP_TYPES.ROAD_VIEW);
  const [showModal, setShowModal] = useState(false);
  const [value, setValue] = useState("");
  const markerSize = isDesktop ? 48 : 36;

  const handleOpenModal = () => {
    setShowModal(true);
    gtagEventEmitter({
      action: "map_mobile_view_modal_opened",
      category: PAGE.INVENTORY,
      label: "",
    });
    logAppEvent({
      eventName: APP_EVENTS.CTA_CLICKED,
      payload: {
        cta_name: CLICKED_ITEM.OPEN_MAP,
        screen_name: APP_PAGE.PROPERTY,
        section: "map_section",
      },
    });
  };
  const handleCloseModal = () => setShowModal(false);

  const {
    universities,
    setUniversities,
    mapType,
    setMapType,
    setQueryUniversityData,
    setNearbyLocation,
    center,
    nearbyLocations,
    nearbyLocationMarkers,
    regionSearchValue,
    map,
    setMap,
    setCenter,
    inventoryMarker,
    isDesktopStreetView,
    regionMarker,
    setDistance,
    selectedUniversityFromLocal,
    showDistanceCard,
    setShowDistanceCard,
  } = useLocationContext();

  const [options, setOptions] = useState([]);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [tabs, setTabs] = useState<any[]>(INITIAL_TABS);
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [selectedOption, setSelectedOption] = useState({ name: "" });

  const ref = useRef(null);
  const { t } = useTranslationHook();
  const updateTabState = (val: any) => {
    setSelectedOption({ name: "" });
    setActiveTab(val);
    updateNearbyLocationMarkers(val);
    gtagEventEmitter({
      action: "nearby_location",
      category: PAGE.INVENTORY,
      label: val.name,
    });
  };

  const handleOptionClick = (item: any, isDropdown = false) => {
    if (isDropdown) {
      setValue(item?.name);
    }
    setShowOptions(false);
    setSelectedOption(item);
    const updatedItem = isDropdown
      ? {
          ...(item || {}),
          lat: item?.location?.location_coordinates?.lat,
          lng: item?.location?.location_coordinates?.lng,
        }
      : {
          ...item,
          location: {
            location_coordinates: {
              lat: item?.lat,
              lng: item?.lng,
            },
          },
        };
    calculateDistance(
      inventory,
      updatedItem,
      map,
      regionMarker,
      setDistance,
      inventoryMarker,
      isDropdown,
    );
    const itemObj = {
      name: activeTab.name,
      items: [updatedItem],
      // isSelected: true,
    };
    updateNearbyLocationMarkers(itemObj, isDropdown);
    if (isDropdown) {
      gtagEventEmitter({
        action: "university_selected_Map_section",
        category: PAGE.INVENTORY,
        label: item.name,
      });
    } else {
      gtagEventEmitter({
        action: "map_tabs_option_clicked",
        category: PAGE.INVENTORY,
        label: activeTab.name,
        value: item.name,
      });
    }
    setTimeout(() => {
      setShowDistanceCard(true);
    }, 0);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      setShowOptions(true);
      const val = e.target.value;
      setValue(val);
      if (e.target.value === "") {
        regionMarker.current.setMap(null);
        setDistance({});
      }
      const filteredOptions = universities.filter((uni: any) =>
        uni?.name?.toLowerCase()?.includes(val?.toLowerCase()),
      );
      const updatedOptions = filteredOptions.length === 0 ? universities : filteredOptions;
      setOptions(updatedOptions);
    } catch (err) {
      setOptions(universities);
    }
  };

  useClickOutside(ref, () => setShowOptions(false));

  const [isMapScriptLoaded, setMapScriptLoaded] = useState<boolean>(false);
  const { inventory } = useSelector(
    ({ propertyPageData }: AppState) => ({
      inventory: propertyPageData?.data,
    }),
    shallowEqual,
  );
  const searchRegionId = useCookies("searchRegionId");
  // eslint-disable-next-line no-restricted-globals
  const initialUniversityId = isNaN(parseInt(searchRegionId, 10))
    ? -1
    : parseInt(searchRegionId, 10);

  const [defaultUniversityData, setDefaultUniversityData] = useState<any>([]);

  const getInitialListOfUniversities = async () => {
    try {
      const res = await getRegionsWithSearchName("", true);

      if (!res || res.length === 0) {
        throw new Error("No universities found.");
      }

      const initialUniIndex = res.findIndex((uni: any) => uni.id === initialUniversityId);

      if (initialUniIndex === -1 && initialUniversityId !== -1) {
        try {
          const univData = await getRegion(`${initialUniversityId}`);
          setQueryUniversityData(univData.data?.data);
          setDefaultUniversityData([...res, univData.data?.data]);
          setUniversities([...res, univData.data?.data]);
        } catch (error) {
          console.error("Error fetching specific university data:", error);
        }
      } else {
        setQueryUniversityData(res[initialUniIndex]);
        setDefaultUniversityData(res);
        setUniversities(res);
      }
    } catch (error) {
      console.error("Error fetching the list of universities:", error);
    }
  };

  const getRegionsWithSearchName = async (name: any, forInitial = false) => {
    if (name || forInitial) {
      const regionOptions = {
        url_path: "/api/v0/regions",
        params: {
          limit: 25,
          sort_key: "distance",
          sort_order: "asc",
          region_point_sort: [
            inventory?.location_coordinates?.lat,
            inventory?.location_coordinates?.lng,
          ].join(","),
          region_type: "establishment",
          region_inventory_id_point: inventory.id,
        },
      };
      // @ts-ignore
      if (name) regionOptions.params.name = name;

      const { data } = await getRegions(regionOptions);

      return data?.data.result;
    }
    return defaultUniversityData;
  };

  const updateUniversitiesOnSearch = async () => {
    const listOfUniv = await getRegionsWithSearchName(regionSearchValue);
    setUniversities(listOfUniv);
  };

  const resetAllLocationFilters = () => {
    setNearbyLocation(null);
    nearbyLocations.current = [];
    if (regionMarker && regionMarker.current && typeof regionMarker.current.setMap !== "undefined")
      regionMarker.current.setMap(null);
    nearbyLocationMarkers.current = nearbyLocationMarkers.current.map((marker: any) =>
      marker.setMap(null),
    );
    nearbyLocationMarkers.current = [];
  };

  const updateNearbyLocationMarkers = (selectedSection: any, isDropdown = false) => {
    resetAllLocationFilters();
    const mapMarkerUrl = getMapMarkerIconUrl(
      isDropdown ? "Universities" : selectedSection?.name,
      selectedSection?.isSelected,
    );
    if (map && typeof google.maps !== "undefined") {
      const bounds = new google.maps.LatLngBounds();
      selectedSection?.items?.forEach((item: any) => {
        const position =
          typeof google !== "undefined" &&
          typeof google.maps !== "undefined" &&
          typeof google.maps.LatLng !== "undefined"
            ? new google.maps.LatLng(item.lat, item.lng)
            : null;
        const marker = new google.maps.Marker({
          position,
          title: item.name,
          icon: {
            url: mapMarkerUrl,
            scaledSize: new google.maps.Size(markerSize, markerSize),
          },
        });
        nearbyLocationMarkers.current = [...nearbyLocationMarkers.current, marker];
        marker.setMap(map);
        bounds.extend(position);
      });
      bounds.extend(center);
      map.fitBounds(bounds);
      map.setCenter(bounds.getCenter());
    }
  };

  // This effect is used to initialize the map script when the component mounts.
  useEffect(() => {
    getInitialListOfUniversities();
    const scriptEl = document.createElement("script");
    scriptEl.setAttribute(
      "src",
      "https://maps.googleapis.com/maps/api/js?v=quarterly&key=AIzaSyAHJ5nH6Gyx2wWTh15V3h0ro8aO24KXNAA&v=3.22&libraries=places,marker,maps",
    );
    scriptEl.nonce = (window as any)?.__INITIAL_STATE__?.appData?.nonce || undefined;
    scriptEl.setAttribute("type", "text/javascript");
    scriptEl.setAttribute("defer", "true");
    document.head.appendChild(scriptEl);
    scriptEl.onload = () => setMapScriptLoaded(true);
  }, []);

  // This effect is used to initialize the map when the component mounts or modal mounts or map type changes.
  useEffect(() => {
    if (isMapScriptLoaded && mapType === "ROAD_VIEW") {
      initializeMap();
    }
  }, [isMapScriptLoaded, showModal, mapType]);

  // This effect is used to build the map when the component mounts or modal mounts or map type changes.
  useEffect(() => {
    if (map && isMapScriptLoaded) {
      buildMap();
    }
  }, [map, isMapScriptLoaded]);

  // This effect runs only on mobile - this is to fix location markers not updating due to Modal creating a second instance of map and not attaching markers onto the same
  useEffect(() => {
    if (
      !isDesktop &&
      map &&
      showModal &&
      activeTab &&
      activeTab.name === "Universities" &&
      activeTab.items.length > 0
    ) {
      setTimeout(() => {
        updateNearbyLocationMarkers(activeTab);
      }, 0);
    }

    if (!isDesktop && map && !showModal && activeTab && activeTab.items.length > 0) {
      setTimeout(() => {
        updateNearbyLocationMarkers(activeTab);
      }, 0);
    }
  }, [activeTab, map, showModal]);

  // This effect runs only on desktop - this is to fix location markers not updating due to changing road view to street view and back
  useEffect(() => {
    if (isDesktop && map && mapType === "ROAD_VIEW") {
      setTimeout(() => {
        updateNearbyLocationMarkers(activeTab);
      }, 0);
    }
  }, [activeTab, map, mapType]);

  // This effect is used to reset the map when the component when Distance card is hidden.
  useEffect(() => {
    if (!showDistanceCard) {
      setSelectedOption({ name: "" });
      if (
        regionMarker &&
        regionMarker.current &&
        typeof regionMarker.current.setMap !== "undefined"
      )
        regionMarker.current.setMap(null);
      setTimeout(() => {
        updateNearbyLocationMarkers(activeTab);
        setValue("");
      }, 0);
    }
  }, [showDistanceCard]);

  const initializeMap = () => {
    if (mapLoaded() && isMapScriptLoaded && typeof google.maps !== "undefined") {
      setCenter(
        new google.maps.LatLng(
          inventory.location_coordinates.lat,
          inventory.location_coordinates.lng,
        ),
      );
      const head: any = document.getElementsByTagName("head")[0];
      const { insertBefore } = head;
      // eslint-disable-next-line func-names
      head.insertBefore = function (
        newElement: { href: string | string[] },
        referenceElement: any,
      ) {
        if (
          newElement.href &&
          newElement.href.indexOf("//fonts.googleapis.com/css?family=Roboto") > -1
        ) {
          return;
        }
        insertBefore.call(head, newElement, referenceElement);
      };

      const mapOptions = {
        mapId: MAP_ID,
        scrollwheel: true,
        zoom: 15,
        zoomControl: isDesktop,
        zoomControlOptions: { position: google?.maps?.ControlPosition?.RIGHT_BOTTOM },
        mapTypeControl: false,
        streetViewControl: false,
        streetViewControlOptions: { position: google?.maps?.ControlPosition?.LEFT_TOP },
        fullscreenControl: false,
        center,
        mapTypeId: google?.maps?.MapTypeId?.ROADMAP,
      };
      setMap(new google.maps.Map(document.getElementById("map2"), mapOptions));
    }
  };
  const buildMap = () => {
    if (mapLoaded() && isMapScriptLoaded && typeof google.maps !== "undefined") {
      if (
        inventory.location_coordinates &&
        inventory.location_coordinates.lat &&
        inventory.location_coordinates.lng
      ) {
        const position = new google.maps.LatLng(
          inventory.location_coordinates.lat,
          inventory.location_coordinates.lng,
        );
        inventoryMarker.current = new google.maps.Marker({
          icon: {
            url: getMapMarkerIconUrl("Property"),
            scaledSize: new google.maps.Size(markerSize, markerSize),
          },
          position,
          map,
        });

        map?.panTo(position);
      }
    }
  };

  useEffect(() => {
    updateUniversitiesOnSearch();
  }, [regionSearchValue]);
  useEffect(() => {
    if (selectedUniversityFromLocal && !isEmptyObject(universities)) {
      const filteredOptions = getFilteredUniversityFromLocal(
        universities,
        selectedUniversityFromLocal,
      );
      if (filteredOptions) {
        handleOptionClick(filteredOptions, true);
      }
    }
  }, [selectedUniversityFromLocal, universities, showModal]);

  return (
    <>
      <SectionContainer
        title={t("propertypage.components.mapsectionv2.nearbylocationsandmaptitle")}
        id="map"
        className={formatClassNames(styles.container, isDesktop && styles.desktop)}
        contentClassName={!isDesktop ? styles.contentContainer : ""}
        showTitleLine
        headingLevel={2}
      >
        <div className={formatClassNames(styles.toggleTabContainer, isDesktop && styles.desktop)}>
          <ToggleTab mapType={mapType} setMapType={setMapType} />
        </div>
        {!isDesktop && (
          <Map
            isDesktop={isDesktop}
            onClick={handleOpenModal}
            inventory={inventory}
            isMapLoaded={isMapScriptLoaded}
          />
        )}
        <MapInfoTabs
          onClick={handleOpenModal}
          isDesktop={isDesktop}
          inventory={inventory}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          handleOptionClick={handleOptionClick}
          selectedOption={selectedOption}
          tabs={tabs}
          setTabs={setTabs}
          updateTabState={updateTabState}
          isMapLoaded={isMapScriptLoaded}
          isDesktopStreetView={isDesktopStreetView}
        />
      </SectionContainer>

      {/* Mobile section - inside modal */}
      {!isDesktop && showModal && (
        <MapModal
          title={t("propertypage.components.mapsectionv2.explorelocationtitle")}
          onClose={handleCloseModal}
        >
          <div className={styles.mapContainerModal}>
            <Map
              isDesktop={isDesktop}
              onClick={handleOpenModal}
              inventory={inventory}
              isMapLoaded={isMapScriptLoaded}
              insideModal
              onOptionClick={handleOptionClick}
              showModal={showModal}
            />
          </div>
          <div className={styles.searchMobileContainer} ref={ref}>
            <div className={styles.searchInputMobileContainer}>
              <SearchIcon fill="#6B7280" />
              <input
                type="search"
                placeholder={t(
                  "propertypage.components.mapsectionv2.searchforuniversitiesplaceholder",
                )}
                className={styles.searchInputMobile}
                value={value}
                onChange={handleOnChange}
              />
            </div>
            {showOptions && options && options.length > 0 && (
              <div className={styles.optionsMobile}>
                {options.map((option: any) => (
                  <div
                    role="none"
                    className={styles.optionMobile}
                    key={option.name}
                    onClick={() => handleOptionClick(option, true)}
                  >
                    {option?.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <MapInfoTabs
            isDesktop={isDesktop}
            inventory={inventory}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleOptionClick={handleOptionClick}
            selectedOption={selectedOption}
            tabs={tabs}
            setTabs={setTabs}
            updateTabState={updateTabState}
            isMapLoaded={isMapScriptLoaded}
            insideModal
            isDesktopStreetView={isDesktopStreetView}
          />
        </MapModal>
      )}
    </>
  );
};

export default wrapperHOC(MapLocationV2, {
  componentName: "PropertyPage-MapSectionV2",
  showForChina: true,
});
